<template>
  <div class="create">
    <form
      class="create__container"
      @submit.prevent="onSubmit"
    >
      <p class="h3">
        Создания события
      </p>

      <InputWrapper title="Выберите площадку">
        <SearchField @select="onSelectField" />
        <InvalidFeedback v-if="v$.createData.$dirty && v$.createData.field.required.$invalid">
          Заполните это поле.
        </InvalidFeedback>
      </InputWrapper>

      <InputWrapper title="Название события">
        <BaseInput v-model="createData.eventName" />
        <InvalidFeedback v-if="v$.createData.$dirty && v$.createData.eventName.required.$invalid">
          Заполните это поле.
        </InvalidFeedback>
      </InputWrapper>

      <InputWrapper title="Начало события">
        <div class="create-date">
          <BaseDatepicker
            v-model="createData.date"
            :disabled="!createData.field"
            :disabledDates="computedDisabledDates"
            @update:modelValue="initCalendarHours"
          />

          <div class="create-date__time">
            с
            <TimeRange
              v-model="startModel"
              :times="startOptions"
              :disabled="!createData.field || !createData.date"
              ref="first"
              class="create-date__time-input"
            />
          </div>
          <div class="create-date__time">
            до
            <TimeRange
              v-model="endModel"
              :times="endOptions"
              :disabled="!createData.field || !createData.date || !startModel"
              ref="second"
              class="create-date__time-input"
            />
          </div>
        </div>
        <InvalidFeedback
          v-if="
            (v$.createData.$dirty && v$.createData.date.required.$invalid) ||
            (v$.createData.$dirty && v$.createData.start.required.$invalid) ||
            (v$.createData.$dirty && v$.createData.end.required.$invalid)
          "
        >
          Заполните это поле.
        </InvalidFeedback>
      </InputWrapper>

      <InputWrapper title="Уровень игры">
        <BaseSelect
          v-model="createData.level"
          :options="options.level"
        />
        <InvalidFeedback v-if="v$.createData.$dirty && v$.createData.level.required.$invalid">
          Заполните это поле.
        </InvalidFeedback>
      </InputWrapper>

      <InputWrapper title="Статус игры">
        <BaseSelect
          v-model="createData.status"
          :options="options.status"
        />
        <InvalidFeedback v-if="v$.createData.$dirty && v$.createData.status.required.$invalid">
          Заполните это поле.
        </InvalidFeedback>
      </InputWrapper>

      <InputWrapper title="Количество участников">
        <BaseInput v-model="createData.countMembers" type="number" />
        <InvalidFeedback v-if="v$.createData.$dirty && v$.createData.countMembers.required.$invalid">
          Заполните это поле.
        </InvalidFeedback>
      </InputWrapper>

      <InputWrapper title="Игроки без приложения">
        <BaseInput v-model="createData.countMembersWithoutApp" type="number" />
        <InvalidFeedback v-if="v$.createData.$dirty && v$.createData.countMembersWithoutApp.required.$invalid">
          Заполните это поле.
        </InvalidFeedback>
      </InputWrapper>

      <InputWrapper>
        <BaseCheckbox
          v-model="createData.hasChat"
          label="Добавить чат в событие"
        />
      </InputWrapper>

      <InputWrapper title="Комментарий к событию">
        <BaseTextArea
          v-model="createData.comment"
          style="height: 200px;"
        />
      </InputWrapper>

      <div class="btn-container">
        <BaseButton
          class="btn-container__item"
        >
          ДОБАВИТЬ
        </BaseButton>
        <BaseButton
          theme="simple"
          class="btn-container__item"
          type="reset"
          @click="resetValues"
        >
          Сбросить
        </BaseButton>
      </div>
    </form>
  </div>
</template>

<script>
import moment from 'moment'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

import BaseDatepicker from '@/components/Base/BaseDatepicker'
import BaseInput from '@/components/Base/BaseInput'
import BaseButton from '@/components/Base/BaseButton'
import InvalidFeedback from '@/components/InvalidFeedback'
import BaseSelect from '@/components/Base/BaseSelect'
import InputWrapper from '@/components/InputWrapper'
import BaseTextArea from '@/components/Base/BaseTextArea'
import BaseCheckbox from '@/components/Base/BaseCheckbox'
import SearchField from '@/components/clever/SearchField'
import TimeRange from '@/components/TimeRange'
import useTimeRange from '@/composables/useTimeRange'

export default {
  name: 'createPage',
  components: {
    BaseDatepicker,
    TimeRange,
    SearchField,
    BaseCheckbox,
    BaseTextArea,
    BaseInput,
    BaseButton,
    InvalidFeedback,
    BaseSelect,
    InputWrapper
  },
  setup () {
    const {
      initialOptions,
      startModel,
      startOptions,
      endModel,
      endOptions
    } = useTimeRange()

    return {
      initialOptions,
      startModel,
      startOptions,
      endModel,
      endOptions,
      v$: useVuelidate()
    }
  },
  data () {
    return {
      availableWeekdays: [],
      createData: {
        field: null,
        eventName: null,
        date: null,
        start: null,
        end: null,
        level: null,
        status: null,
        countMembers: null,
        countMembersWithoutApp: null,
        hasChat: true,
        comment: null
      },
      options: {
        status: [
          { value: 0, text: 'Завершена' },
          { value: 1, text: 'Текущий' },
          { value: 2, text: 'Предстоящий' }
        ],
        level: [
          { value: 0, text: 'Начинающий' },
          { value: 1, text: 'Любительский' },
          { value: 2, text: 'Продвинутый' }
        ]
      }
    }
  },
  computed: {
    requestBody () {
      return {
        id: this.createData.field,
        title: this.createData.eventName,
        date: moment(this.createData.date).format('DD-MM-YYYY'),
        startKey: this.createData.start,
        endKey: this.createData.end,
        gameLevel: this.createData.level,
        status: this.createData.status,
        players: Number(this.createData.countMembers),
        offlinePlayers: Number(this.createData.countMembersWithoutApp),
        chat: this.createData.hasChat,
        comment: this.createData.comment
      }
    }
  },
  methods: {
    async onSubmit () {
      if (this.v$.createData.$invalid) {
        this.v$.createData.$touch()
        return
      }

      try {
        const { data } = await this.$api.events.create(this.requestBody)
        if (data?.eventId) {
          this.$router.push('/events/' + data?.eventId)
        }
      } catch (e) {
        alert('Произошла ошибка')
        console.log(e)
      }
    },
    computedDisabledDates (event) {
      return !this.availableWeekdays.includes(event.getDay() - 1)
    },
    resetValues () {
      return setImmediate(() => {
        this.createData.field = null
        this.createData.eventName = null
        this.createData.date = null
        this.createData.start = null
        this.createData.end = null
        this.createData.level = null
        this.createData.status = null
        this.createData.countMembers = null
        this.createData.countMembersWithoutApp = null
        this.createData.hasChat = true
        this.createData.comment = null

        this.v$.form.$reset()
      })
    },
    async initCalendarHours () {
      const fieldId = this.createData.field
      const date = this.requestBody.date

      const res = await this.$api.events.calendarHours(fieldId, date)
      const calendar = res?.data?.calendar ?? []

      this.initialOptions = calendar.map(item => {
        return {
          value: item.key,
          text: item.value,
          disabled: item.disabled
        }
      })
    },
    onSelectField ({ value, availableWeekdays }) {
      this.createData.field = value
      this.availableWeekdays = availableWeekdays
    }
  },
  watch: {
    startModel (val) {
      this.$refs.first.onClose()
      setImmediate(() => {
        this.$refs.second.onOpen()
      })

      this.createData.start = val
    },
    endModel (val) {
      this.createData.end = val
    }
  },
  validations () {
    return {
      createData: {
        field: {
          required
        },
        eventName: {
          required
        },
        date: {
          required
        },
        start: {
          required
        },
        end: {
          required
        },
        level: {
          required
        },
        status: {
          required
        },
        countMembers: {
          required
        },
        countMembersWithoutApp: {
          required
        },
        hasChat: {},
        comment: {}
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.create {
  &__container {
    max-width: 800px;
  }

  &-date {
    display: flex;
    align-items: center;

    &__time {
      display: flex;
      align-items: center;
      margin-left: 16px;

      &-input {
        width: 100px;
        margin-left: 14px;
      }
    }
  }

  .btn-container {
    margin-top: 40px;

    &__item {
      height: 50px;
    }
  }
}
</style>
